import React, { useMemo } from 'react'
import { Button } from '@/atoms/Button'
import { CheckBox } from '@/atoms/CheckBox'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { CaptionMD, CaptionSM, HeadingXS, ParagraphMD, ParagraphSM, TitleMD } from '@/atoms/Text'
import { useToggle } from '@/hooks/useToggle'
import { useUser } from '@/services/UserService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface ConfirmationStepProps {
  handleGoBack: () => void
  onClose: () => void
  handleGiftConfirm: () => Promise<void>
  projectName: string
  isLoading: boolean
  theatricalSlug: string
}

export const ConfirmStep = ({
  handleGoBack,
  onClose,
  handleGiftConfirm,
  projectName,
  isLoading = false,
  theatricalSlug,
}: ConfirmationStepProps) => {
  const { t } = useTranslate('common')
  const [isOptedIn, toggleIsOptedIn] = useToggle(false)
  const { isLoggedIn } = useUser()
  const { discountCodes } = useGuildTickets(theatricalSlug)
  const { isReady, query } = useSafeRouter()

  const urlTicketCodes = useMemo(() => {
    const queryDiscounts = getItemOrFirstEntry(query?.discounts)
    return queryDiscounts ? queryDiscounts.split(',').filter(Boolean) : []
  }, [query?.discounts])

  const effectiveDiscountCodes = useMemo(() => {
    return isLoggedIn ? discountCodes : urlTicketCodes
  }, [isLoggedIn, discountCodes, urlTicketCodes])

  const ticketQuantity = useMemo(() => {
    return effectiveDiscountCodes?.length ?? 0
  }, [effectiveDiscountCodes])

  if (!isReady) {
    return null
  }

  return (
    <>
      <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-black/10 p-1.5" onClick={onClose}>
        <CloseIcon color="black" size={18} />
      </div>
      <FlexColumn className="gap-8 pb-6 md:pb-8">
        <FlexColumn className="gap-2">
          <div className="mx-auto mb-6 hidden rounded-full border-2 border-[#A6EBD94D] bg-oxide-100 p-[1.125rem] md:block">
            <PayItForwardOutlineIcon size={28} color="oxide" />
          </div>
          <div className="mx-auto mb-6 rounded-full border-2 border-[#A6EBD94D] bg-oxide-100 p-4 md:hidden">
            <PayItForwardOutlineIcon size={24} color="oxide" />
          </div>
          <HeadingXS weight="bold" className="hidden leading-[2rem] -tracking-[0.03125rem] md:block md:text-[1.75rem]">
            {t('giftTicketsModalHeading', 'Let Angel Gift My Tickets')}
          </HeadingXS>
          <TitleMD weight={'bold'} className={'md:hidden'}>
            {t('giftTicketsModalHeading', 'Let Angel Gift My Tickets')}{' '}
          </TitleMD>

          <ParagraphMD
            weight={'medium'}
            className={
              'hidden text-[0.875rem] leading-[1.625rem] text-core-gray-700 md:block md:text-base md:leading-[1.75rem]'
            }
            color={'gray-700'}
          >
            {t(
              'giftTicketsWarning',
              `You're about to gift your ({{ numberOfCodes }}) two unused Premium Guild tickets to {{ projectName }}.`,
              {
                numberOfCodes: ticketQuantity,
                projectName: projectName,
              },
            )}
          </ParagraphMD>

          <ParagraphSM weight={'medium'} className={' text-core-gray-700 md:hidden'} color={'gray-700'}>
            {t(
              'giftTicketsWarning',
              `You're about to gift your ({{ numberOfCodes }}) two unused Premium Guild tickets to {{ projectName }}.`,
              {
                numberOfCodes: ticketQuantity,
                projectName: projectName,
              },
            )}
          </ParagraphSM>
        </FlexColumn>
      </FlexColumn>
      <div className="flex items-start gap-2">
        <div className="max-h-6 shrink-0 pt-1">
          <CheckBox
            checked={isOptedIn}
            checkboxSize={22}
            labelClassName="w-fit"
            wrapperClassName={'!max-h-6'}
            onChange={() => toggleIsOptedIn(!isOptedIn)}
            uncheckedClassName="!border-core-gray-400 !border !rounded-[0.1875rem] cursor-pointer items-start !max-h-6 !max-w-6"
            checkedClassName="border-core-gray-950 !border !bg-core-gray-950 !rounded-[0.1875rem] cursor-pointer !max-h-6 !max-w-6"
          />{' '}
        </div>
        <CaptionMD weight="medium" className={'hidden text-left md:block'} color={'gray-700'}>
          {t(
            'giftTicketsTerms',
            `I understand I'm gifting my ({{ numberOfCodes }}) two unused Guild tickets to someone else, allowing them to experience {{ projectName }} in theaters. Angel will redistribute on my behalf. This can't be undone.`,
            {
              numberOfCodes: ticketQuantity,
              projectName: projectName,
            },
          )}
        </CaptionMD>
        <CaptionSM weight={'medium'} className={'text-left md:hidden'} color={'gray-700'}>
          {t(
            'giftTicketsTerms',
            `I understand I'm gifting my ({{ numberOfCodes }}) two unused Guild tickets to someone else, allowing them to experience {{ projectName }} in theaters. Angel will redistribute on my behalf. This can't be undone.`,
            {
              numberOfCodes: ticketQuantity,
              projectName: projectName,
            },
          )}
        </CaptionSM>
      </div>

      <FlexColumn className="gap-2 pt-6">
        <FlexColumn className=" items-center justify-center gap-2">
          <Button
            onClick={handleGiftConfirm}
            className="w-full items-center gap-1.5 rounded-[0.875rem] px-5 py-3 text-sm font-medium md:max-w-44"
            variant="oxide"
            disabled={!isOptedIn || isLoading}
          >
            {isLoading ? t('processing', 'Processing...') : t('confirmGift', 'Gift My Tickets')}
          </Button>
          <Button
            onClick={handleGoBack}
            className="font-color-black w-full items-center gap-1.5 rounded-[0.875rem] px-5 py-3 text-sm font-medium md:hidden"
            variant="gray"
          >
            {t('goBack', 'Go Back')}
          </Button>
          <Button
            onClick={handleGoBack}
            className="font-color-black hidden items-center gap-1.5 rounded-[0.875rem] px-5 py-3 text-sm font-medium md:flex"
            variant="white"
          >
            {t('goBack', 'Go Back')}
          </Button>
        </FlexColumn>
      </FlexColumn>
    </>
  )
}
